import React, { useMemo, useState } from "react";
import {
  Chart as ChartJS, CategoryScale, LinearScale,
  PointElement, LineElement, BarElement, ArcElement,
  Tooltip, Legend, Filler,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Line, Bar, Doughnut, Pie } from "react-chartjs-2";

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Tooltip, Legend, Filler, ChartDataLabels
);
ChartJS.defaults.set("plugins.datalabels", { display: false });

// ── Design tokens ──────────────────────────────────────────────────────────────
const C = {
  primary:   "#4338ca",   // indigo-700
  primary2:  "#6366f1",   // indigo-500
  primary_l: "rgba(67,56,202,0.08)",
  success:   "#10b981",
  warning:   "#f59e0b",
  danger:    "#ef4444",
  violet:    "#7c3aed",
  cyan:      "#06b6d4",
  orange:    "#fb923c",
  text:      "#111827",
  text2:     "#6b7280",
  text3:     "#9ca3af",
  border:    "#e5e7eb",
  bg:        "#f9fafb",
  card:      "#ffffff",
  shadow:    "0 1px 6px rgba(0,0,0,0.07), 0 4px 16px rgba(0,0,0,0.04)",
};

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmt = (n) =>
  new Intl.NumberFormat("vi-VN", { style: "currency", currency: "VND" }).format(n || 0);

const fmtShort = (n) => {
  if (!n || n === 0) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(0)}k`;
  return String(Math.round(n));
};

const buyerKey = (o) => o.buyer?.phone || o.buyer?.email || o.buyer?.name || "";

const filterByPeriod = (arr, period) => {
  if (period === "all") return arr;
  const cutoff = new Date();
  if      (period === "today") cutoff.setHours(0, 0, 0, 0);
  else if (period === "week")  cutoff.setDate(cutoff.getDate() - 7);
  else if (period === "month") { cutoff.setDate(1); cutoff.setHours(0, 0, 0, 0); }
  return arr.filter(o => new Date(o.createdAt || 0) >= cutoff);
};

const getPrevPeriod = (arr, period) => {
  if (period === "all" || period === "today") return [];
  const end   = new Date();
  const start = new Date();
  if (period === "week") {
    end.setDate(end.getDate() - 7);
    start.setDate(start.getDate() - 14);
  } else {
    end.setDate(1); end.setHours(0, 0, 0, 0);
    start.setMonth(start.getMonth() - 1); start.setDate(1); start.setHours(0, 0, 0, 0);
  }
  return arr.filter(o => {
    const d = new Date(o.createdAt || 0);
    return d >= start && d < end;
  });
};

const trend = (cur, prev) => {
  if (!prev || prev === 0) return null;
  return ((cur - prev) / prev) * 100;
};

const last7Days = () => {
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    days.push(d.toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit" }));
  }
  return days;
};

const last6Months = () => {
  const ms = [];
  const now = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    ms.push({ label: d.toLocaleDateString("vi-VN", { month: "2-digit", year: "2-digit" }), month: d.getMonth(), year: d.getFullYear() });
  }
  return ms;
};

// ── Main Component ─────────────────────────────────────────────────────────────
export default function Dashboard({ orders = [], analytics = {} }) {
  const [period, setPeriod] = useState("month");

  const periodOrders = useMemo(() => filterByPeriod(orders, period), [orders, period]);
  const prevOrders   = useMemo(() => getPrevPeriod(orders, period), [orders, period]);

  // ── KPIs nhóm 1: Doanh thu & Đơn hàng ──────────────────────────────────────
  const kpi = useMemo(() => {
    const calc = (arr) => {
      const success = arr.filter(o => o.status === "verified_success");
      const rev     = success.reduce((s, o) => s + (o.total || 0), 0);
      const cnt     = success.length;
      const aov     = cnt > 0 ? rev / cnt : 0;
      const rate    = arr.length > 0 ? (cnt / arr.length) * 100 : 0;

      // Sub-breakdown revenue — theo phương thức TT (3 nhóm exhaustive, luôn cộng = rev)
      const codRev   = success.filter(o => o.paymentMethod === "cod").reduce((s, o) => s + (o.total||0), 0);
      const bankRev  = success.filter(o => o.paymentMethod === "bank_transfer").reduce((s, o) => s + (o.total||0), 0);
      const otherRev = rev - codRev - bankRev; // COD + bank + khác = tổng, không overlap

      // Sub-breakdown orders by status — 4 nhóm exhaustive (luôn cộng = arr.length)
      const verified = cnt;                                                                              // đã duyệt
      const pending  = arr.filter(o => o.status === "pending_confirmation").length;
      const paid     = arr.filter(o => o.status === "customer_paid_checking").length;
      const rejected = arr.filter(o => o.status === "payment_rejected").length;
      const otherCnt = arr.length - verified - pending - paid - rejected;                               // mọi status khác

      // AOV details
      const totals   = success.map(o => o.total || 0);
      const maxOrder = totals.length ? Math.max(...totals) : 0;
      const minOrder = totals.length ? Math.min(...totals) : 0;
      const sorted   = [...totals].sort((a, b) => a - b);
      const median   = sorted.length ? sorted[Math.floor(sorted.length / 2)] : 0;

      return { rev, cnt, aov, rate, codRev, bankRev, otherRev, verified, pending, paid, rejected, otherCnt, maxOrder, minOrder, median };
    };
    return { cur: calc(periodOrders), prev: calc(prevOrders) };
  }, [periodOrders, prevOrders]);

  // ── KPIs nhóm 2: Khách hàng ──────────────────────────────────────────────────
  const custKpi = useMemo(() => {
    const success = orders.filter(o => o.status === "verified_success");
    const buyerMap = {};
    success.forEach(o => {
      const k = buyerKey(o);
      if (!k) return;
      if (!buyerMap[k]) buyerMap[k] = { name: o.buyer?.name || k, orders: [], revenue: 0 };
      buyerMap[k].orders.push(o);
      buyerMap[k].revenue += o.total || 0;
    });
    const buyers     = Object.values(buyerMap);
    const unique     = buyers.length;
    const returning  = buyers.filter(b => b.orders.length >= 2).length;
    const newCount   = unique - returning;
    const retention  = unique > 0 ? (returning / unique) * 100 : 0;
    const clv        = unique > 0 ? buyers.reduce((s, b) => s + b.revenue, 0) / unique : 0;
    const maxClv     = buyers.length ? Math.max(...buyers.map(b => b.revenue)) : 0;
    const avgOrders  = unique > 0 ? success.length / unique : 0;

    const times = orders
      .filter(o => o.status === "verified_success" && o.createdAt && o.approvedAt)
      .map(o => (new Date(o.approvedAt) - new Date(o.createdAt)) / 60000);
    const avgMin = times.length ? times.reduce((a, b) => a + b, 0) / times.length : 0;
    const minMin = times.length ? Math.min(...times) : 0;
    const maxMin = times.length ? Math.max(...times) : 0;

    const now = new Date();
    const pOrders = filterByPeriod(success, period);
    const thisKeys = new Set(pOrders.map(buyerKey).filter(Boolean));
    const firstBuy = {};
    [...success].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
      .forEach(o => { const k = buyerKey(o); if (k && !firstBuy[k]) firstBuy[k] = new Date(o.createdAt); });
    const newInPeriod = [...thisKeys].filter(k => {
      if (!buyerMap[k]) return false;
      const f = firstBuy[k];
      const cutoff = new Date();
      if (period === "today") cutoff.setHours(0,0,0,0);
      else if (period === "week") cutoff.setDate(cutoff.getDate()-7);
      else if (period === "month") { cutoff.setDate(1); cutoff.setHours(0,0,0,0); }
      else return true;
      return f >= cutoff;
    }).length;

    const top5 = [...buyers].sort((a, b) => b.revenue - a.revenue).slice(0, 5);

    const fmtMin = (m) => {
      if (!m) return "N/A";
      if (m < 60) return `${Math.round(m)} phút`;
      const h = Math.floor(m/60), mm = Math.round(m%60);
      return mm > 0 ? `${h}g ${mm}p` : `${h} giờ`;
    };

    return { unique, returning, newCount, retention, clv, maxClv, avgOrders, avgMin, minMin, maxMin, top5, newInPeriod, fmtMin };
  }, [orders, period, periodOrders]);

  // ── Chart data ──────────────────────────────────────────────────────────────
  const revenueData = useMemo(() => {
    const labels = last7Days();
    const map = {}; labels.forEach(l => (map[l] = 0));
    orders.filter(o => o.status === "verified_success" && o.createdAt).forEach(o => {
      const l = new Date(o.createdAt).toLocaleDateString("vi-VN", { day:"2-digit", month:"2-digit" });
      if (map[l] !== undefined) map[l] += o.total || 0;
    });
    return {
      labels,
      datasets: [{
        label: "Doanh thu",
        data: labels.map(l => map[l]),
        borderColor: C.primary,
        backgroundColor: C.primary_l,
        borderWidth: 2.5,
        pointBackgroundColor: C.primary,
        pointRadius: 4, fill: true, tension: 0.4,
      }],
    };
  }, [orders]);

  const statusData = useMemo(() => {
    const m = { pending_confirmation:0, customer_paid_checking:0, verified_success:0, payment_rejected:0 };
    orders.forEach(o => { if (m[o.status]!==undefined) m[o.status]++; else m.pending_confirmation++; });
    return {
      labels: ["Chờ xác nhận","Đã chuyển khoản","Đã duyệt","Bị từ chối"],
      datasets: [{ data:[m.pending_confirmation,m.customer_paid_checking,m.verified_success,m.payment_rejected],
        backgroundColor:["#fbbf24","#38bdf8","#10b981","#ef4444"], borderWidth:0, hoverOffset:4 }],
    };
  }, [orders]);

  const topProductData = useMemo(() => {
    const map = {};
    orders.forEach(o => { (o.items||[]).forEach(it => { const n=it.nameVi||it.name||"?"; map[n]=(map[n]||0)+(it.quantity||1); }); });
    const sorted = Object.entries(map).sort((a,b)=>b[1]-a[1]).slice(0,8);
    return {
      labels: sorted.map(([k])=>k),
      datasets: [{ label:"Số lượng", data:sorted.map(([,v])=>v),
        backgroundColor: sorted.map((_,i)=>`rgba(67,56,202,${1-i*0.1})`), borderRadius:5 }],
    };
  }, [orders]);

  const paymentData = useMemo(() => {
    const m = {cod:0,bank_transfer:0,other:0};
    orders.forEach(o=>{ if(o.paymentMethod==="cod")m.cod++; else if(o.paymentMethod==="bank_transfer")m.bank_transfer++; else m.other++; });
    return {
      labels:["COD (Tiền mặt)","Chuyển khoản","Khác"],
      datasets:[{ data:[m.cod,m.bank_transfer,m.other], backgroundColor:["#4338ca","#10b981","#94a3b8"], borderWidth:0 }],
    };
  }, [orders]);

  const hourlyData = useMemo(() => {
    const hours = Array(24).fill(0);
    orders.forEach(o=>{ if(o.createdAt) hours[new Date(o.createdAt).getHours()]++; });
    const max = Math.max(...hours);
    return {
      labels: Array.from({length:24},(_,i)=>`${String(i).padStart(2,"0")}h`),
      datasets:[{ data:hours, backgroundColor:hours.map(v=>v===max?C.primary:"rgba(67,56,202,0.18)"), borderRadius:3 }],
    };
  }, [orders]);

  const retentionChartData = useMemo(() => {
    const success = orders.filter(o=>o.status==="verified_success");
    const months  = last6Months();
    const firstBuy = {};
    [...success].sort((a,b)=>new Date(a.createdAt)-new Date(b.createdAt))
      .forEach(o=>{ const k=buyerKey(o); if(k&&!firstBuy[k]) firstBuy[k]=new Date(o.createdAt); });
    const forMonth = (m) => {
      const keys = new Set();
      success.filter(o=>{ const d=new Date(o.createdAt||0); return d.getMonth()===m.month&&d.getFullYear()===m.year; })
             .forEach(o=>{ const k=buyerKey(o); if(k) keys.add(k); });
      return keys;
    };
    return {
      labels: months.map(m=>m.label),
      datasets:[
        { label:"Khách mới",      data:months.map(m=>{ const k=forMonth(m); return [...k].filter(k=>{ const f=firstBuy[k]; return f&&f.getMonth()===m.month&&f.getFullYear()===m.year; }).length; }), backgroundColor:C.cyan,    borderRadius:4 },
        { label:"Khách quay lại", data:months.map(m=>{ const k=forMonth(m); return [...k].filter(k=>{ const f=firstBuy[k]; return f&&!(f.getMonth()===m.month&&f.getFullYear()===m.year); }).length; }), backgroundColor:C.primary, borderRadius:4 },
      ],
    };
  }, [orders]);

  // ── Chart options ───────────────────────────────────────────────────────────
  const lineOpts = {
    responsive:true, maintainAspectRatio:false,
    plugins:{
      legend:{ display:false },
      tooltip:{ backgroundColor:"#1e1b4b", padding:12, cornerRadius:8, callbacks:{ label:ctx=>`  ${fmt(ctx.parsed.y)}` } },
      datalabels:{
        display: ctx => { const v = ctx.dataset.data[ctx.dataIndex]; return typeof v==="number" && v>0; },
        align:"top", anchor:"end",
        formatter: v => { const n=Number(v)||0; return n>=1e6?`${(n/1e6).toFixed(1)}M`:`${(n/1e3).toFixed(0)}k`; },
        color: C.primary,
        font:{ size:10, weight:"700", family:"Inter,system-ui,sans-serif" },
        padding: { bottom: 4 },
      },
    },
    scales:{
      y:{ ticks:{ callback:v=>fmtShort(v), font:{size:11,family:"Inter,system-ui,sans-serif"}, color:C.text3 }, grid:{color:"rgba(0,0,0,0.03)"}, border:{display:false} },
      x:{ ticks:{ font:{size:10,family:"Inter,system-ui,sans-serif"}, color:C.text3 }, grid:{display:false}, border:{display:false} },
    },
    layout:{ padding:{ top:24 } },
  };

  const barHOpts = {
    responsive:true, maintainAspectRatio:false, indexAxis:"y",
    plugins:{
      legend:{display:false},
      datalabels:{
        display:ctx=>{ const v=ctx.dataset.data[ctx.dataIndex]; return typeof v==="number"&&v>0; },
        anchor:"end", align:"right",
        formatter:v=>`${Number(v)||0}`,
        color:C.text2, font:{size:11,weight:"600",family:"Inter,system-ui,sans-serif"},
      },
    },
    scales:{ x:{grid:{color:"rgba(0,0,0,0.03)"},ticks:{font:{size:10},color:C.text3},border:{display:false}}, y:{grid:{display:false},ticks:{font:{size:11,family:"Inter,system-ui,sans-serif"},color:C.text},border:{display:false}} },
    layout:{padding:{right:40}},
  };

  const stackedOpts = {
    responsive:true, maintainAspectRatio:false,
    plugins:{
      legend:{ position:"bottom", labels:{font:{size:11,family:"Inter,system-ui,sans-serif"},padding:16,usePointStyle:true,pointStyleWidth:8} },
      datalabels:{
        display: ctx => { const v = ctx.dataset.data[ctx.dataIndex]; return typeof v==="number" && v>0; },
        formatter: v => String(Number(v)||0),
        color:"#fff",
        font:{ size:11, weight:"700", family:"Inter,system-ui,sans-serif" },
      },
    },
    scales:{
      x:{ stacked:true, grid:{display:false}, ticks:{font:{size:10},color:C.text3}, border:{display:false} },
      y:{ stacked:true, grid:{color:"rgba(0,0,0,0.03)"}, ticks:{precision:0,font:{size:10},color:C.text3}, border:{display:false} },
    },
  };

  const hourOpts = {
    responsive:true, maintainAspectRatio:false,
    plugins:{
      legend:{display:false},
      datalabels:{
        display:ctx=>{ const v=ctx.dataset.data[ctx.dataIndex]; return typeof v==="number"&&v>0; },
        anchor:"end", align:"top",
        formatter:v=>String(Number(v)||0),
        color:C.primary, font:{size:9,weight:"700",family:"Inter,system-ui,sans-serif"},
      },
    },
    scales:{ y:{grid:{color:"rgba(0,0,0,0.03)"},ticks:{font:{size:10},color:C.text3},border:{display:false}}, x:{grid:{display:false},ticks:{font:{size:9},color:C.text3},border:{display:false}} },
    layout:{padding:{top:16}},
  };

  const _arcDatalabels = {
    display: ctx => { const v = ctx.dataset.data[ctx.dataIndex]; return typeof v==="number" && v>0; },
    formatter: (v, ctx) => {
      const total = ctx.dataset.data.reduce((a,b) => a+(Number(b)||0), 0);
      const pct   = total>0 ? ((Number(v)/total)*100).toFixed(0) : "0";
      return [`${v}`, `${pct}%`];
    },
    color:"#fff",
    font:{ size:11, weight:"700", family:"Inter,system-ui,sans-serif" },
    textAlign:"center",
  };

  const donutOpts = {
    responsive:true, maintainAspectRatio:false,
    plugins:{
      legend:{ position:"right", labels:{font:{size:11,family:"Inter,system-ui,sans-serif"},padding:14,usePointStyle:true,pointStyleWidth:8} },
      tooltip:{ backgroundColor:"#1e1b4b", padding:10, cornerRadius:8 },
      datalabels: _arcDatalabels,
    },
    cutout:"62%",
  };

  const pieOpts = {
    responsive:true, maintainAspectRatio:false,
    plugins:{
      legend:{ position:"right", labels:{font:{size:11,family:"Inter,system-ui,sans-serif"},padding:14,usePointStyle:true,pointStyleWidth:8} },
      tooltip:{ backgroundColor:"#1e1b4b", padding:10, cornerRadius:8 },
      datalabels: _arcDatalabels,
    },
  };

  const tRev   = trend(kpi.cur.rev,    kpi.prev.rev);
  const tCnt   = trend(kpi.cur.cnt,    kpi.prev.cnt);
  const tAov   = trend(kpi.cur.aov,    kpi.prev.aov);
  const tRate  = trend(kpi.cur.rate,   kpi.prev.rate);

  const periodLabel = { today:"Hôm nay", week:"Tuần", month:"Tháng này", all:"Tất cả" };

  return (
    <div style={s.wrap}>
      {/* Google Fonts Inter */}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');`}</style>

      {/* ── Header ── */}
      <div style={s.header}>
        <div>
          <div style={s.headerTitle}>Analytics Dashboard</div>
          <div style={s.headerSub}>Phân tích hiệu quả kinh doanh · {new Date().toLocaleDateString("vi-VN",{day:"numeric",month:"long",year:"numeric"})}</div>
        </div>
        <div style={s.tabs}>
          {["today","week","month","all"].map(p => (
            <button key={p} onClick={()=>setPeriod(p)}
              style={period===p ? s.tabActive : s.tabInactive}>
              {periodLabel[p]}
            </button>
          ))}
        </div>
      </div>

      {/* ── Row 1: Doanh thu & Đơn hàng ── */}
      <div style={s.sectionTitle}>Doanh thu &amp; Đơn hàng</div>
      <div style={s.kpiRow}>
        <BigKpiCard
          label="Tổng doanh thu"
          value={fmt(kpi.cur.rev)}
          trendVal={tRev}
          // 3 nhóm payment method exhaustive → tổng = tổng DT (không overlap)
          subs={[
            { dot:"#4338ca", label:"COD (Tiền mặt)",  val: fmt(kpi.cur.codRev),   pct: kpi.cur.rev>0?kpi.cur.codRev/kpi.cur.rev*100:0 },
            { dot:"#10b981", label:"Chuyển khoản",    val: fmt(kpi.cur.bankRev),  pct: kpi.cur.rev>0?kpi.cur.bankRev/kpi.cur.rev*100:0 },
            { dot:"#9ca3af", label:"Khác/chưa xác nhận", val: fmt(kpi.cur.otherRev), pct: kpi.cur.rev>0?kpi.cur.otherRev/kpi.cur.rev*100:0 },
          ]}
        />
        <BigKpiCard
          // "X thành công / Y tổng" — X=verified_success, Y=tất cả đơn trong kỳ
          label="Đơn hàng (thành công / tổng)"
          value={`${kpi.cur.cnt} / ${periodOrders.length}`}
          trendVal={tCnt}
          // 4 status exhaustive + otherCnt → luôn cộng = periodOrders.length
          subs={[
            { dot:"#10b981", label:"Đã duyệt",       val: `${kpi.cur.verified} đơn`, pct: periodOrders.length>0?kpi.cur.verified/periodOrders.length*100:0 },
            { dot:"#fbbf24", label:"Chờ xác nhận",   val: `${kpi.cur.pending} đơn`,  pct: periodOrders.length>0?kpi.cur.pending/periodOrders.length*100:0  },
            { dot:"#38bdf8", label:"Đã chuyển khoản",val: `${kpi.cur.paid} đơn`,     pct: periodOrders.length>0?kpi.cur.paid/periodOrders.length*100:0     },
            { dot:"#ef4444", label:"Bị từ chối",     val: `${kpi.cur.rejected} đơn`, pct: periodOrders.length>0?kpi.cur.rejected/periodOrders.length*100:0 },
            ...(kpi.cur.otherCnt > 0 ? [{ dot:"#9ca3af", label:"Trạng thái khác", val: `${kpi.cur.otherCnt} đơn`, pct:0 }] : []),
          ]}
        />
        <BigKpiCard
          label="Giá trị đơn TB (AOV)"
          value={fmt(kpi.cur.aov)}
          trendVal={tAov}
          subs={[
            { dot:"#10b981", label:"Đơn cao nhất",  val: fmtShort(kpi.cur.maxOrder)+"đ" },
            { dot:"#f59e0b", label:"Trung vị",       val: fmtShort(kpi.cur.median)+"đ"   },
            { dot:"#ef4444", label:"Đơn thấp nhất", val: fmtShort(kpi.cur.minOrder)+"đ" },
          ]}
        />
        <BigKpiCard
          label="Tỷ lệ thành công"
          value={`${kpi.cur.rate.toFixed(1)}%`}
          trendVal={tRate}
          subs={[
            { dot:"#10b981", label:"Đã duyệt", val: `${kpi.cur.cnt} đơn`          },
            { dot:"#6b7280", label:"Tổng đơn", val: `${periodOrders.length} đơn`  },
            { dot:"#4338ca", label:"Mục tiêu", val: "≥ 70%"                        },
          ]}
        />
      </div>

      {/* ── Row 2: Khách hàng ── */}
      <div style={s.sectionTitle}>Khách hàng</div>
      <div style={s.kpiRow}>
        <BigKpiCard
          label="Tỷ lệ quay lại"
          value={`${custKpi.retention.toFixed(1)}%`}
          trendVal={null}
          subs={[
            { dot:"#4338ca", label:"Khách quay lại", val: `${custKpi.returning} người` },
            { dot:"#38bdf8", label:"Khách mới",       val: `${custKpi.newCount} người`  },
            { dot:"#9ca3af", label:"Tổng unique",      val: `${custKpi.unique} người`   },
          ]}
        />
        <BigKpiCard
          label="CLV trung bình"
          value={fmt(custKpi.clv)}
          trendVal={null}
          subs={[
            { dot:"#10b981", label:"CLV cao nhất",  val: fmtShort(custKpi.maxClv)+"đ"           },
            { dot:"#4338ca", label:"TB đơn/khách",  val: `${custKpi.avgOrders.toFixed(1)} đơn`  },
            { dot:"#f59e0b", label:"Khách kỳ này",  val: `${custKpi.newInPeriod} mới`           },
          ]}
        />
        <BigKpiCard
          label="Tg duyệt đơn TB"
          value={custKpi.fmtMin(custKpi.avgMin)}
          trendVal={null}
          subs={[
            { dot:"#10b981", label:"Nhanh nhất",   val: custKpi.fmtMin(custKpi.minMin) },
            { dot:"#ef4444", label:"Chậm nhất",    val: custKpi.fmtMin(custKpi.maxMin) },
            { dot:"#9ca3af", label:"Số đơn đo",    val: `${orders.filter(o=>o.approvedAt).length} đơn` },
          ]}
        />
        <BigKpiCard
          label="Số sessions"
          value={(analytics.sessions||0).toLocaleString("vi-VN")}
          trendVal={null}
          subs={[
            { dot:"#f59e0b", label:"Xem giỏ hàng", val: (analytics.cart_views||0).toLocaleString("vi-VN") },
            { dot:"#4338ca", label:"Đặt hàng",      val: (analytics.checkouts||0).toLocaleString("vi-VN") },
            {
              dot: analytics.sessions ? "#10b981" : "#9ca3af",
              label:"CR ước tính",
              val: analytics.sessions
                ? `${((analytics.checkouts||0)/analytics.sessions*100).toFixed(2)}%`
                : "Đang thu thập",
            },
          ]}
        />
      </div>

      {/* ── Charts Row 1: Revenue trend + Order status ── */}
      <div style={s.chartRow2}>
        <div style={{...s.card, flex:2}}>
          <div style={s.cardHeader}>
            <span style={s.cardTitle}>Doanh thu 7 ngày gần nhất</span>
            <span style={s.cardBadge}>{fmt(kpi.cur.rev)} tổng</span>
          </div>
          <div style={{height:210, position:"relative"}}><Line data={revenueData} options={lineOpts}/></div>
        </div>
        <div style={{...s.card, flex:1}}>
          <div style={s.cardHeader}><span style={s.cardTitle}>Trạng thái đơn hàng</span></div>
          <div style={{height:210, position:"relative"}}><Doughnut data={statusData} options={donutOpts}/></div>
        </div>
      </div>

      {/* ── Chart Row 2: Top products ── */}
      <div style={s.card}>
        <div style={s.cardHeader}><span style={s.cardTitle}>Top sản phẩm bán chạy</span></div>
        <div style={{height:260, position:"relative"}}><Bar data={topProductData} options={barHOpts}/></div>
      </div>

      {/* ── Chart Row 3: Payment + Hourly ── */}
      <div style={s.chartRow2}>
        <div style={{...s.card, flex:1}}>
          <div style={s.cardHeader}><span style={s.cardTitle}>Phương thức thanh toán</span></div>
          <div style={{height:210, position:"relative"}}><Pie data={paymentData} options={pieOpts}/></div>
        </div>
        <div style={{...s.card, flex:2}}>
          <div style={s.cardHeader}>
            <span style={s.cardTitle}>Đơn hàng theo giờ</span>
            <span style={s.cardSub}>Peak hours → bổ sung nhân lực</span>
          </div>
          <div style={{height:210, position:"relative"}}><Bar data={hourlyData} options={hourOpts}/></div>
        </div>
      </div>

      {/* ── Chart Row 4: New vs Returning ── */}
      <div style={s.card}>
        <div style={s.cardHeader}>
          <span style={s.cardTitle}>Khách mới vs Khách quay lại (6 tháng)</span>
          <span style={s.cardBadge}>{custKpi.retention.toFixed(1)}% retention</span>
        </div>
        <div style={{height:200, position:"relative"}}><Bar data={retentionChartData} options={stackedOpts}/></div>
      </div>

      {/* ── Top Customers Table ── */}
      {custKpi.top5.length > 0 && (
        <div style={s.card}>
          <div style={s.cardHeader}>
            <span style={s.cardTitle}>🏆 Top 5 Khách hàng VIP</span>
            <span style={s.cardSub}>Theo tổng doanh thu</span>
          </div>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead>
              <tr>
                {["#","Khách hàng","Số đơn","Tổng chi tiêu","CLV / đơn"].map(h=>(
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {custKpi.top5.map((c,i)=>(
                <tr key={i} style={{background: i%2===0 ? "#fafafa" : "#fff"}}>
                  <td style={s.td}>
                    <span style={{fontWeight:700, fontSize:15, color:i===0?"#fbbf24":i===1?"#94a3b8":"#b45309"}}>
                      {i===0?"🥇":i===1?"🥈":i===2?"🥉":`#${i+1}`}
                    </span>
                  </td>
                  <td style={{...s.td, fontWeight:600, color:C.text}}>{c.name}</td>
                  <td style={s.td}>
                    <span style={{background:C.primary_l, color:C.primary, borderRadius:20, padding:"3px 10px", fontSize:12, fontWeight:600}}>
                      {c.orders.length} đơn
                    </span>
                  </td>
                  <td style={{...s.td, fontWeight:700, color:C.primary}}>{fmt(c.revenue)}</td>
                  <td style={{...s.td, color:C.success, fontWeight:600}}>{fmt(c.revenue/c.orders.length)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── BigKpiCard ─────────────────────────────────────────────────────────────────
function BigKpiCard({ label, value, trendVal, subs = [] }) {
  return (
    <div style={s.bigKpi}>
      <div style={s.bigKpiLabel}>{label}</div>
      <div style={{display:"flex", alignItems:"baseline", gap:10, marginBottom:8}}>
        <div style={s.bigKpiValue}>{value}</div>
        {trendVal !== null && trendVal !== undefined && (
          <div style={{
            fontSize:12, fontWeight:600, borderRadius:20, padding:"2px 8px",
            background: trendVal>=0 ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)",
            color: trendVal>=0 ? "#059669" : "#dc2626",
          }}>
            {trendVal>=0?"▲":"▼"} {Math.abs(trendVal).toFixed(1)}%
          </div>
        )}
      </div>
      <div style={{borderTop:`1px solid ${C.border}`, paddingTop:10, marginTop:4}}>
        {subs.map((sub,i)=>(
          <div key={i} style={s.subItem}>
            <span style={{width:7,height:7,borderRadius:"50%",background:sub.dot,flexShrink:0}}/>
            <span style={s.subLabel}>{sub.label}</span>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              {sub.pct !== undefined && sub.pct > 0 && (
                <span style={{fontSize:10,color:C.text3,fontFamily:FONT}}>
                  {sub.pct.toFixed(0)}%
                </span>
              )}
              <span style={s.subVal}>{sub.val}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────
const FONT = "'Inter', system-ui, -apple-system, sans-serif";

const s = {
  wrap:        { paddingBottom:40, fontFamily:FONT, background:C.bg, padding:"0 0 40px" },
  header:      { display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24, paddingTop:4 },
  headerTitle: { fontSize:22, fontWeight:800, color:C.text, fontFamily:FONT, letterSpacing:"-0.5px" },
  headerSub:   { fontSize:12, color:C.text3, marginTop:3, fontFamily:FONT },
  tabs:        { display:"flex", gap:4, background:"#fff", padding:"4px", borderRadius:12, boxShadow:"0 1px 4px rgba(0,0,0,0.08)" },
  tabActive:   { padding:"7px 16px", borderRadius:8, background:C.primary, color:"#fff", fontSize:12, fontWeight:700, border:"none", cursor:"pointer", fontFamily:FONT, transition:"all 0.15s" },
  tabInactive: { padding:"7px 16px", borderRadius:8, background:"transparent", color:C.text2, fontSize:12, fontWeight:500, border:"none", cursor:"pointer", fontFamily:FONT, transition:"all 0.15s" },
  sectionTitle:{ fontSize:11, fontWeight:700, color:C.text3, letterSpacing:"1.5px", textTransform:"uppercase", marginBottom:12 },
  kpiRow:      { display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:16, marginBottom:24 },
  bigKpi:      { background:C.card, borderRadius:16, padding:"20px 22px", boxShadow:C.shadow, border:`1px solid ${C.border}` },
  bigKpiLabel: { fontSize:11, fontWeight:600, color:C.text3, textTransform:"uppercase", letterSpacing:"0.8px", marginBottom:8, fontFamily:FONT },
  bigKpiValue: { fontSize:26, fontWeight:800, color:C.text, fontFamily:FONT, letterSpacing:"-1px", lineHeight:1.1 },
  subItem:     { display:"flex", alignItems:"center", gap:8, padding:"5px 0", borderBottom:`1px solid ${C.border}` },
  subLabel:    { fontSize:12, color:C.text2, flex:1, fontFamily:FONT },
  subVal:      { fontSize:12, fontWeight:700, color:C.text, fontFamily:FONT },
  chartRow2:   { display:"flex", gap:16, marginBottom:16 },
  card:        { background:C.card, borderRadius:16, padding:"20px 24px", boxShadow:C.shadow, border:`1px solid ${C.border}`, marginBottom:16 },
  cardHeader:  { display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 },
  cardTitle:   { fontSize:14, fontWeight:700, color:C.text, fontFamily:FONT },
  cardSub:     { fontSize:11, color:C.text3, fontFamily:FONT },
  cardBadge:   { fontSize:11, fontWeight:600, background:C.primary_l, color:C.primary, padding:"4px 10px", borderRadius:20, fontFamily:FONT },
  th:          { textAlign:"left", fontSize:10, fontWeight:700, color:C.text3, padding:"8px 12px", borderBottom:`2px solid ${C.border}`, textTransform:"uppercase", letterSpacing:"1px", fontFamily:FONT },
  td:          { fontSize:13, padding:"12px 12px", borderBottom:`1px solid ${C.border}`, color:C.text2, fontFamily:FONT },
};